import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/icons';
import './PastRaces.css';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);

// Normalizza per ricerca: minuscolo, senza accenti/diacritici, trim.
const normalize = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

export default function PastRaces() {
  const navigate = useNavigate();
  const [races, setRaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtri
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCircuit, setSelectedCircuit] = useState('ALL');
  const [selectedFormula, setSelectedFormula] = useState('ALL');
  const [sortBy, setSortBy] = useState('date-desc'); // date-desc, date-asc, duration-desc, drivers-desc

  useEffect(() => {
    fetchRaces();
  }, []);

  const fetchRaces = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/recordings`);
      const data = await response.json();

      if (data.ok && data.recordings) {
        setRaces(data.recordings.filter(r => r.summary)); // Solo gare con summary
      } else {
        setError('Errore nel caricamento delle gare');
      }
    } catch (err) {
      console.error('Errore fetch gare:', err);
      setError('Impossibile caricare le gare passate');
    } finally {
      setLoading(false);
    }
  };

  // Estrai circuiti unici
  const uniqueCircuits = useMemo(() => {
    const circuits = new Set();
    races.forEach(r => {
      if (r.summary?.finalData?.circuit?.name) {
        circuits.add(r.summary.finalData.circuit.name);
      }
    });
    return Array.from(circuits).sort();
  }, [races]);

  // Estrai formule uniche
  const uniqueFormulas = useMemo(() => {
    const formulas = new Set();
    races.forEach(r => {
      r.summary?.finalData?.drivers?.forEach(d => {
        if (d.formula?.label) {
          formulas.add(d.formula.label);
        }
      });
    });
    return Array.from(formulas).sort();
  }, [races]);

  // Piloti distinti nell'archivio (solo per il contatore in testa).
  const uniqueDriversCount = useMemo(() => {
    const names = new Set();
    races.forEach(r => {
      r.summary?.finalData?.drivers?.forEach(d => { if (d.fullName) names.add(normalize(d.fullName)); });
    });
    return names.size;
  }, [races]);

  // Filtra e ordina gare
  const filteredRaces = useMemo(() => {
    let filtered = races;

    // Filtro ricerca: circuito, ID gara e NOMI PILOTI. Cercando un pilota
    // escono tutte le gare in cui ha girato (poi ordinate cronologicamente
    // dal sort attivo, di default "più recenti").
    if (searchTerm.trim()) {
      const q = normalize(searchTerm);
      filtered = filtered.filter(r => {
        const fd = r.summary?.finalData;
        if (normalize(fd?.circuit?.name).includes(q)) return true;
        if (normalize(r.summary?.raceId).includes(q)) return true;
        return (fd?.drivers || []).some(d => normalize(d.fullName).includes(q));
      });
    }

    // Filtro circuito
    if (selectedCircuit !== 'ALL') {
      filtered = filtered.filter(r => r.summary?.finalData?.circuit?.name === selectedCircuit);
    }

    // Filtro formula
    if (selectedFormula !== 'ALL') {
      filtered = filtered.filter(r => {
        return r.summary?.finalData?.drivers?.some(d => d.formula?.label === selectedFormula);
      });
    }

    // Ordinamento
    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.summary?.startTime || 0) - new Date(a.summary?.startTime || 0);
        case 'date-asc':
          return new Date(a.summary?.startTime || 0) - new Date(b.summary?.startTime || 0);
        case 'duration-desc':
          return (b.summary?.durationSeconds || 0) - (a.summary?.durationSeconds || 0);
        case 'drivers-desc':
          return (b.summary?.finalData?.drivers?.length || 0) - (a.summary?.finalData?.drivers?.length || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [races, searchTerm, selectedCircuit, selectedFormula, sortBy]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const formatLapTime = (time) => {
    if (!time || time === null) return 'N/A';
    const minutes = Math.floor(time / 60);
    const seconds = (time % 60).toFixed(3);
    return minutes > 0 ? `${minutes}:${seconds.padStart(6, '0')}` : `${seconds}s`;
  };

  // Solo presentazione: la data lunga ("02 settembre 2026 alle ore 09:49")
  // viene mostrata su due righe (giorno / ora) nella tabella.
  const splitDate = (dateString) => {
    const full = formatDate(dateString);
    const parts = full.split(' alle ore ');
    return parts.length === 2 ? parts : [full, ''];
  };

  const header = (
    <header className="rs-page-header">
      <div className="rs-page-header__text">
        <span className="rs-kicker rs-kicker--red"><span className="rs-kicker__dot" />Archivio</span>
        <h1 className="rs-page-header__title">Gare <em>passate</em></h1>
        <p className="rs-page-header__subtitle">Registrazioni complete: classifiche, tempi giro, settori e traiettorie.</p>
      </div>
    </header>
  );

  if (loading) {
    return (
      <div className="rs-page arch-page">
        {header}
        <div className="rs-empty arch-empty">
          <span className="rs-spinner" />
          <strong>Caricamento archivio</strong>
          <p>Lettura delle registrazioni salvate sul server…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rs-page arch-page">
        {header}
        <div className="rs-empty arch-empty">
          <Icon.Alert />
          <strong>Archivio non disponibile</strong>
          <p>{error}</p>
          <button className="rs-btn rs-btn--outline" onClick={fetchRaces}>Riprova</button>
        </div>
      </div>
    );
  }

  return (
    <div className="rs-page arch-page">
      {header}

      {/* Toolbar: ricerca + filtri */}
      <div className="arch-toolbar">
        <div className="rs-field arch-toolbar__search">
          <label className="rs-label" htmlFor="arch-search">Ricerca</label>
          <div className="rs-search">
            <Icon.Search />
            <input
              id="arch-search"
              type="text"
              className="rs-input"
              placeholder="Pilota, circuito o ID gara…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="rs-field">
          <label className="rs-label" htmlFor="arch-circuit">Circuito</label>
          <select id="arch-circuit" className="rs-select" value={selectedCircuit} onChange={(e) => setSelectedCircuit(e.target.value)}>
            <option value="ALL">Tutti i circuiti</option>
            {uniqueCircuits.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div className="rs-field">
          <label className="rs-label" htmlFor="arch-formula">Formula</label>
          <select id="arch-formula" className="rs-select" value={selectedFormula} onChange={(e) => setSelectedFormula(e.target.value)}>
            <option value="ALL">Tutte le formule</option>
            {uniqueFormulas.map(f => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </div>

        <div className="rs-field">
          <label className="rs-label" htmlFor="arch-sort">Ordina per</label>
          <select id="arch-sort" className="rs-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="date-desc">Data (più recenti)</option>
            <option value="date-asc">Data (più vecchie)</option>
            <option value="duration-desc">Durata</option>
            <option value="drivers-desc">Numero piloti</option>
          </select>
        </div>
      </div>

      {/* Contatori */}
      <section className="rs-grid rs-grid--4 arch-stats" aria-label="Riepilogo archivio">
        <div className="rs-stat rs-stat--inline">
          <span className="rs-stat__value">{filteredRaces.length}<small>di {races.length}</small></span>
          <span className="rs-stat__label">Gare</span>
        </div>
        <div className="rs-stat rs-stat--inline">
          <span className="rs-stat__value">{uniqueCircuits.length}</span>
          <span className="rs-stat__label">Circuiti</span>
        </div>
        <div className="rs-stat rs-stat--inline">
          <span className="rs-stat__value">{uniqueDriversCount}</span>
          <span className="rs-stat__label">Piloti</span>
        </div>
        <div className="rs-stat rs-stat--inline">
          <span className="rs-stat__value">{uniqueFormulas.length}</span>
          <span className="rs-stat__label">Formule</span>
        </div>
      </section>

      {/* Tabella gare */}
      {filteredRaces.length === 0 ? (
        <div className="rs-empty arch-empty">
          <Icon.Archive />
          <strong>Nessuna gara trovata</strong>
          <p>Nessuna registrazione corrisponde ai filtri selezionati.</p>
        </div>
      ) : (
        <div className="rs-table-wrap arch-table-wrap">
          <table className="rs-table rs-table--dense arch-table">
            <thead>
              <tr>
                <th className="arch-col-n">#</th>
                <th>Data</th>
                <th>Evento</th>
                <th>Circuito</th>
                <th>Tipo</th>
                <th className="num">Piloti</th>
                <th className="num rs-hide-mobile">Giri</th>
                <th className="num rs-hide-mobile">Durata</th>
                <th className="num">Best lap</th>
                <th>Vincitore</th>
                <th className="arch-col-action" aria-label="Azioni" />
              </tr>
            </thead>
            <tbody>
              {filteredRaces.map((race, idx) => {
                const summary = race.summary;
                const finalData = summary?.finalData;
                const circuit = finalData?.circuit;
                const drivers = finalData?.drivers || [];
                const winner = drivers.find(d => d.position === 1);
                const [dateDay, dateTime] = splitDate(summary.startTime);
                const isQuali = finalData?.eventType === 'qualifying';

                return (
                  <tr
                    key={race.folder}
                    className="is-clickable"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/gare-passate/${race.folder}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/gare-passate/${race.folder}`);
                      }
                    }}
                  >
                    <td className="arch-col-n"><span className="arch-index">{idx + 1}</span></td>
                    <td>
                      <div className="arch-date">
                        <span className="arch-date__day">{dateDay}</span>
                        {dateTime && <span className="arch-date__time">{dateTime}</span>}
                      </div>
                    </td>
                    <td>
                      <div className="arch-event">
                        <span className="arch-event__name">{finalData?.eventName || 'Sessione senza nome'}</span>
                        {summary?.raceId && <span className="arch-event__id">{summary.raceId}</span>}
                      </div>
                    </td>
                    <td>
                      <div className="arch-circuit">
                        <span className="arch-circuit__name">{circuit?.name || 'Circuito sconosciuto'}</span>
                        <span className="arch-circuit__len">
                          {circuit?.stats?.lengthMeters ? `${(circuit.stats.lengthMeters / 1000).toFixed(2)} km` : 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`rs-badge ${isQuali ? 'rs-badge--info' : 'rs-badge--red'}`}>
                        {isQuali ? 'Qualifica' : 'Gara'}
                      </span>
                    </td>
                    <td className="num">{drivers.length}</td>
                    <td className="num rs-hide-mobile">{finalData?.totalLaps || 'N/A'}</td>
                    <td className="num rs-hide-mobile">{formatDuration(summary.durationSeconds)}</td>
                    <td className="num">
                      <span className={`rs-time ${finalData?.globalBestLap ? 'rs-time--best' : 'arch-na'}`}>
                        {formatLapTime(finalData?.globalBestLap)}
                      </span>
                    </td>
                    <td>
                      {winner ? (
                        <div className="arch-winner">
                          <Icon.Trophy size={14} />
                          <span className="arch-winner__name">{winner.fullName}</span>
                          <span className="arch-winner__formula">{winner.formula?.label || 'No Formula'}</span>
                        </div>
                      ) : <span className="arch-na">—</span>}
                    </td>
                    <td className="arch-col-action">
                      <button
                        className="rs-btn rs-btn--outline rs-btn--sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/gare-passate/${race.folder}`);
                        }}
                      >
                        Analizza <Icon.ArrowRight size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
